import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, FileText, Loader2, Plus, Upload, X } from "lucide-react";
import MetadataReviewRow from "./MetadataReviewRow";
import PdfPane from "../components/PdfPane";
import { readStandardDocuments } from "./api";
import {
  ConfirmedStandard,
  ProposedField,
  ProposedMetadata,
  ReadResult,
  StandardDocument,
  StandardType,
} from "./types";

interface UploadStandardModalProps {
  /** Set when the modal was opened by dropping a file on the library. */
  initialFile?: File | null;
  onCancel: () => void;
  onSubmit: (confirmed: ConfirmedStandard) => Promise<void>;
}

/** The fields a person has to sign off before a standard joins the library. */
const REVIEW_FIELDS = [
  "jurisdictionPath",
  "authority",
  "title",
  "edition",
  "shortCode",
  "domain",
] as const;

type ReviewField = (typeof REVIEW_FIELDS)[number];

const HINTS: Record<ReviewField, string> = {
  jurisdictionPath:
    "Where the standard has force, outermost first. A municipality is usually named on the cover; the province and country rarely are.",
  authority: "The body that issues it, as a citation would name it.",
  title: "The standard's own name, without the authority attached to it.",
  edition:
    "As the authority names this version. Rules bind to an edition, never to a standard in general — so a wrong edition mislabels every rule under it.",
  shortCode:
    "The library's code for this standard. It prefixes every rule ID from it, e.g. 10001COLL-DS-7.6-001. Change it if you would rather it read differently.",
  domain: "The library's filter facet — the field of practice, not the document's own chapters.",
};

const LABELS: Record<ReviewField, string> = {
  jurisdictionPath: "Jurisdiction",
  authority: "Authority",
  title: "Standard",
  edition: "Edition",
  shortCode: "Short code",
  domain: "Domain",
};

type Phase = "choose" | "reading" | "verify";

/** The values being confirmed, kept apart from what the reader proposed. */
interface Draft {
  jurisdictionPath: string[];
  authority: string;
  title: string;
  edition: string;
  shortCode: string;
  domain: string;
  type: StandardType;
  relevance: string;
}

const EMPTY_DRAFT: Draft = {
  jurisdictionPath: [""],
  authority: "",
  title: "",
  edition: "",
  shortCode: "",
  domain: "",
  type: "ingested",
  relevance: "",
};

function draftFrom(metadata: ProposedMetadata): Draft {
  return {
    jurisdictionPath: metadata.jurisdictionPath.map((f) => f.value).filter(Boolean),
    authority: metadata.authority.value,
    title: metadata.title.value,
    edition: metadata.edition.value,
    shortCode: metadata.shortCode.value,
    domain: metadata.domain.value,
    type: "ingested",
    relevance: "",
  };
}

/**
 * Adding a standard, in two steps: the document is read, then every field it proposed is confirmed
 * one at a time.
 *
 * The document arrives the way SMRT COP's RFDS does — supplied, not searched for — and the review
 * works the way TOSP's lease review works, a row at a time, because a field nobody looked at is a
 * field nobody is accountable for. Where the reader found nothing the row still has to be settled:
 * you type the value and confirm it. Same act, different starting point.
 */
const UploadStandardModal = ({ initialFile, onCancel, onSubmit }: UploadStandardModalProps) => {
  const [phase, setPhase] = useState<Phase>("choose");
  /** The parts of ONE standard, in the order they were added. Part 0 carries the cover page. */
  const [files, setFiles] = useState<File[]>(initialFile ? [initialFile] : []);
  /**
   * The second-document question, asked BEFORE the picker opens.
   *
   * A second file is ambiguous in a way the first is not — it might be volume two of this standard, or
   * an entirely different standard somebody is adding while they are here. Only the person holding it
   * knows, and asking first means someone who meant "a new standard" never goes looking for a file
   * they were not going to be allowed to add.
   *
   * "ask" is the question; "declined" is the answer that ends here — standards are added one at a
   * time, so the way forward is to finish this one.
   */
  const [askAnother, setAskAnother] = useState<null | "ask" | "declined">(null);
  const [read, setRead] = useState<ReadResult | null>(null);
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [verified, setVerified] = useState<Set<string>>(new Set());
  const [openRow, setOpenRow] = useState<ReviewField | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const filePicker = useRef<HTMLInputElement>(null);

  // What the document pane is looking at. Kept here rather than derived in the pane so that re-opening
  // the same row jumps again — the page has not changed, but the reviewer's attention has.
  const [focusDoc, setFocusDoc] = useState(0);
  const [focusPage, setFocusPage] = useState(1);
  const [focusQuote, setFocusQuote] = useState<string | undefined>(undefined);
  const [jumpKey, setJumpKey] = useState(0);

  const readDocuments = useCallback(async (targets: File[]) => {
    setPhase("reading");
    setError(null);
    try {
      const result = await readStandardDocuments(targets);
      setRead(result);
      setDraft(draftFrom(result.metadata));
      // Nothing starts verified, including what the reader was confident about. Confidence is a reason
      // to look, not a substitute for looking.
      setVerified(new Set());
      setPhase("verify");
      // Open the first row and take the document to wherever that value was read from.
      const first = result.metadata.jurisdictionPath.find((level) => level.pdfPage !== undefined);
      setFocusDoc(0);
      setFocusPage(first?.pdfPage ?? 1);
      setFocusQuote(first?.quote);
      setJumpKey((k) => k + 1);
      setOpenRow("jurisdictionPath");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not read the document.");
      setPhase("choose");
    }
  }, []);

  // A file dropped on the library arrives here already chosen. It is NOT read on arrival: dropping
  // picks the document, Next reads it, and those staying separate means a mis-drop costs a click
  // rather than a wasted read.
  useEffect(() => {
    if (initialFile) setFiles([initialFile]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Opens the picker for the first file, or asks what a further one is before opening it. */
  const addFile = () => {
    if (files.length === 0) filePicker.current?.click();
    else setAskAnother("ask");
  };

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((prev) => ({ ...prev, [key]: value }));

  /** Editing a field un-verifies it: the value that was signed off is no longer the value on screen. */
  const edit = <K extends keyof Draft>(key: K, value: Draft[K], field: ReviewField) => {
    set(key, value);
    setVerified((prev) => {
      const next = new Set(prev);
      next.delete(field);
      return next;
    });
  };

  /**
   * Opens a row and takes the document with it.
   *
   * The jump is the point of the split view: a value is only checkable against the page it came from,
   * and making the reviewer find that page themselves is most of the reason field-by-field review gets
   * skipped. A row the reader could not source leaves the document where it is rather than jumping
   * somewhere arbitrary — there is nothing to look at, and moving would imply there was.
   */
  const focusRow = (field: ReviewField | null) => {
    setOpenRow(field);
    if (!field || !read) return;

    const source =
      field === "jurisdictionPath"
        ? read.metadata.jurisdictionPath.find((level) => level.pdfPage !== undefined)
        : read.metadata[field];

    if (source?.pdfPage) {
      setFocusPage(source.pdfPage);
      setFocusQuote(source.quote);
      // Metadata is only ever read from the first part, so that is the part being looked at.
      setFocusDoc(0);
      setJumpKey((k) => k + 1);
    } else {
      setFocusQuote(undefined);
    }
  };

  const confirm = (field: ReviewField) => {
    setVerified((prev) => new Set(prev).add(field));
    const next = REVIEW_FIELDS.find((f) => f !== field && !verified.has(f));
    focusRow(next ?? null);
  };

  const setSegment = (index: number, value: string) =>
    edit(
      "jurisdictionPath",
      draft.jurisdictionPath.map((seg, i) => (i === index ? value : seg)),
      "jurisdictionPath",
    );

  const outstanding = REVIEW_FIELDS.filter((f) => !verified.has(f));

  const submit = async () => {
    const path = draft.jurisdictionPath.map((s) => s.trim()).filter(Boolean);
    if (path.length === 0) return setError("Give the jurisdiction at least one level.");
    if (!draft.authority.trim()) return setError("Name the issuing authority.");
    if (!draft.title.trim()) return setError("Name the standard.");
    if (!draft.edition.trim()) return setError("Give the edition.");
    if (!draft.domain.trim()) return setError("Give the domain.");

    setError(null);
    setSaving(true);
    try {
      // The parts as the server staged them, stripped of the per-file structure it sent back for display.
      const documents: StandardDocument[] = (read?.documents ?? []).map(
        ({ docId, blobName, fileName, fileSize, docIndex }) => ({
          docId,
          blobName,
          fileName,
          fileSize,
          docIndex,
        }),
      );

      await onSubmit({
        ...draft,
        jurisdictionPath: path,
        documents,
        verifiedFields: [...verified],
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not add the standard.");
      setSaving(false);
    }
  };

  const rowProps = (field: ReviewField, display: string) => ({
    label: LABELS[field],
    hint: HINTS[field],
    display,
    verified: verified.has(field),
    open: openRow === field,
    onToggle: () => focusRow(openRow === field ? null : field),
    onConfirm: () => confirm(field),
  });

  const proposed = (field: keyof ProposedMetadata): ProposedField | undefined => {
    if (!read || field === "jurisdictionPath") return undefined;
    return read.metadata[field];
  };

  return (
    <div className="vr-backdrop" role="dialog" aria-modal="true" aria-label="Upload standard">
      {/* Asked before the picker opens, because the answer decides whether there is a file to pick. */}
      {askAnother && (
        <div className="vr-backdrop vr-backdrop-top" role="dialog" aria-modal="true" aria-label="Another document">
          <div className="vr-modal vr-modal-sm">
            <div className="vr-modal-head">
              <h3>Another document</h3>
            </div>

            <div className="vr-modal-body">
              {askAnother === "ask" ? (
                <>
                  <p className="vr-ask-q">
                    Is this part of this <strong>Same</strong> standard, or a <strong>New</strong>{" "}
                    standard?
                  </p>
                  <p className="vr-ask-hint">
                    Parts of one standard share an edition and are structured into a single section
                    tree, each keeping its own page numbering.
                  </p>
                </>
              ) : (
                <p className="vr-ask-q">
                  Let&rsquo;s finish this standard, then do the new one separately.
                </p>
              )}
            </div>

            <div className="vr-modal-foot">
              {askAnother === "ask" ? (
                <>
                  <button className="vr-btn vr-btn-ghost" onClick={() => setAskAnother(null)}>
                    Cancel
                  </button>
                  <button
                    className="vr-btn vr-btn-outline"
                    onClick={() => {
                      setAskAnother(null);
                      filePicker.current?.click();
                    }}
                  >
                    Same
                  </button>
                  <button className="vr-btn vr-btn-glow" onClick={() => setAskAnother("declined")}>
                    New
                  </button>
                </>
              ) : (
                <>
                  <button className="vr-btn vr-btn-ghost" onClick={() => setAskAnother("ask")}>
                    <ArrowLeft size={14} /> Back
                  </button>
                  {/* Exactly what the footer's Next does — the standard already in hand gets read. */}
                  <button
                    className="vr-btn vr-btn-glow"
                    onClick={() => {
                      setAskAnother(null);
                      void readDocuments(files);
                    }}
                  >
                    Next <ArrowRight size={14} />
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Choosing a file is a small act and gets a small dialog. Verifying is reading a document
          against what was taken from it, so the panel opens out and the document comes with it. */}
      <div className={`vr-modal ${phase === "verify" ? "vr-modal-wide" : ""}`}>
        {/* Writing the section tree is hundreds of sequential Cosmos writes, so "Adding…" on the
            button alone leaves the panel looking frozen for several seconds. The overlay says the
            work is happening, roughly how much of it there is, and stops anything being clicked
            underneath while it runs. */}
        {saving && (
          <div className="vr-busy">
            <div className="vr-busy-card">
              <Loader2 size={22} className="vr-spin-inline" />
              <p className="vr-busy-title">Adding to the library</p>
              <p className="vr-busy-sub">
                Writing the section tree for {read?.structure.pageCount ?? 0} pages. This takes a few
                seconds.
              </p>
            </div>
          </div>
        )}

        <div className="vr-modal-head">
          <div>
            <h3>Upload Standard</h3>
          </div>
          <button className="vr-iconbtn" onClick={onCancel} aria-label="Close" disabled={saving}>
            <X size={16} />
          </button>
        </div>

        <div className="vr-modal-body">
          {error && <div className="vr-form-error">{error}</div>}

          {/* ── Step 1: choose the document, then read it ── */}
          {phase !== "verify" && (
            <div className="vr-field">
              <label>Source document</label>

              {files.length > 0 && (
                <div className="vr-file-list">
                  {files.map((f, i) => (
                    <div className="vr-file-row" key={`${f.name}-${i}`}>
                      <FileText size={15} style={{ color: "hsl(var(--vr-primary))" }} />
                      <span className="vr-file-name">
                        {files.length > 1 && <span className="vr-part-no">Part {i + 1}</span>}
                        {f.name}
                      </span>
                      <span className="vr-cell-dim">{(f.size / 1024 / 1024).toFixed(1)} MB</span>
                      {phase === "choose" && (
                        <button
                          className="vr-rowbtn vr-danger"
                          onClick={() => setFiles(files.filter((_, j) => j !== i))}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}


              {phase === "reading" ? (
                <div className="vr-reading">
                  <Loader2 size={16} className="vr-spin-inline" />
                  <span>
                    Reading {files.length > 1 ? `${files.length} parts` : files[0]?.name}…
                  </span>
                </div>
              ) : (
                <>
                  <input
                    ref={filePicker}
                    type="file"
                    accept="application/pdf"
                    style={{ display: "none" }}
                    onChange={(e) => {
                      const picked = e.target.files?.[0];
                      if (picked) setFiles((prev) => [...prev, picked]);
                      // Cleared so re-picking the same file still fires a change event.
                      e.target.value = "";
                    }}
                  />
                  <button
                    type="button"
                    className="vr-btn vr-btn-outline"
                    onClick={addFile}
                  >
                    <FileText size={14} /> {files.length > 0 ? "Add another file" : "Choose File"}
                  </button>
                </>
              )}
            </div>
          )}

          {/* ── Step 2: confirm what was read, beside the document it was read from ── */}
          {phase === "verify" && read && (
            <div className="vr-split">
              <PdfPane
                file={files[focusDoc] ?? files[0] ?? null}
                page={focusPage}
                quote={focusQuote}
                jumpKey={jumpKey}
              />

              <div className="vr-review-pane">
              <div className="vr-file-list">
                {read.documents.map((doc) => (
                  <div className="vr-file-row" key={doc.docId}>
                    <FileText size={15} style={{ color: "hsl(var(--vr-primary))" }} />
                    <span className="vr-file-name">
                      {read.documents.length > 1 && (
                        <span className="vr-part-no">Part {doc.docIndex + 1}</span>
                      )}
                      {doc.fileName}
                    </span>
                    <span className="vr-cell-dim">
                      {doc.structure.pageCount} pages · {doc.structure.headingCount} headings
                      {doc.structure.rasterPageCount > 0 &&
                        ` · ${doc.structure.rasterPageCount} raster`}
                    </span>
                  </div>
                ))}
              </div>

              <div className="vr-review-list">
                <MetadataReviewRow
                  {...rowProps("jurisdictionPath", draft.jurisdictionPath.filter(Boolean).join(" › "))}
                  proposedLevels={read.metadata.jurisdictionPath}
                >
                  <div className="vr-jpath">
                    {draft.jurisdictionPath.map((segment, i) => (
                      <span key={i} style={{ display: "contents" }}>
                        {i > 0 && <span className="vr-jpath-sep">›</span>}
                        <input
                          className="vr-input"
                          value={segment}
                          onChange={(e) => setSegment(i, e.target.value)}
                          placeholder="Level"
                        />
                      </span>
                    ))}
                    <button
                      type="button"
                      className="vr-rowbtn"
                      onClick={() =>
                        edit("jurisdictionPath", [...draft.jurisdictionPath, ""], "jurisdictionPath")
                      }
                    >
                      <Plus size={12} /> Level
                    </button>
                  </div>
                </MetadataReviewRow>

                <MetadataReviewRow
                  {...rowProps("authority", draft.authority)}
                  proposed={proposed("authority")}
                >
                  <input
                    className="vr-input"
                    value={draft.authority}
                    onChange={(e) => edit("authority", e.target.value, "authority")}
                    placeholder="Town of Collingwood"
                  />
                </MetadataReviewRow>

                <MetadataReviewRow {...rowProps("title", draft.title)} proposed={proposed("title")}>
                  <input
                    className="vr-input"
                    value={draft.title}
                    onChange={(e) => edit("title", e.target.value, "title")}
                    placeholder="Development Standards"
                  />
                </MetadataReviewRow>

                <MetadataReviewRow {...rowProps("edition", draft.edition)} proposed={proposed("edition")}>
                  <input
                    className="vr-input"
                    value={draft.edition}
                    onChange={(e) => edit("edition", e.target.value, "edition")}
                    placeholder="June 2026"
                  />
                </MetadataReviewRow>

                <MetadataReviewRow
                  {...rowProps("shortCode", draft.shortCode)}
                  proposed={proposed("shortCode")}
                >
                  <input
                    className="vr-input"
                    value={draft.shortCode}
                    onChange={(e) => edit("shortCode", e.target.value, "shortCode")}
                    placeholder="COLL-DS"
                  />
                </MetadataReviewRow>

                <MetadataReviewRow {...rowProps("domain", draft.domain)} proposed={proposed("domain")}>
                  <input
                    className="vr-input"
                    value={draft.domain}
                    onChange={(e) => edit("domain", e.target.value, "domain")}
                    placeholder="Engineering / Development"
                  />
                </MetadataReviewRow>

              </div>

              <div className="vr-field">
                <label>Relevance (optional)</label>
                <input
                  className="vr-input"
                  value={draft.relevance}
                  onChange={(e) => set("relevance", e.target.value)}
                  placeholder="Municipal servicing and development approvals"
                />
                <span className="vr-field-hint">
                  Why this standard applies here. Not read from the document — it is about your work,
                  not about the document.
                </span>
              </div>
              </div>
            </div>
          )}
        </div>

        {/* Each step ends in the action that belongs to it: choosing ends in Next, reviewing ends in
            Add to Library. A step whose only button is one it cannot use tells you nothing. */}
        <div className="vr-modal-foot">
          {phase === "verify" && outstanding.length > 0 && (
            <span className="vr-foot-note">
              {outstanding.length} field{outstanding.length === 1 ? "" : "s"} still to confirm
            </span>
          )}

          {phase === "verify" && (
            <button
              className="vr-btn vr-btn-ghost"
              onClick={() => {
                setPhase("choose");
                setRead(null);
                setVerified(new Set());
                setOpenRow(null);
                setError(null);
              }}
              disabled={saving}
              title="Go back and choose a different document"
            >
              <ArrowLeft size={14} /> Back
            </button>
          )}

          <button className="vr-btn vr-btn-ghost" onClick={onCancel} disabled={saving}>
            Cancel
          </button>

          {phase !== "verify" ? (
            <button
              className="vr-btn vr-btn-glow"
              onClick={() => files.length > 0 && void readDocuments(files)}
              disabled={files.length === 0 || phase === "reading" || askAnother !== null}
              title={files.length === 0 ? "Choose a document first" : undefined}
            >
              {phase === "reading" ? (
                <>
                  <Loader2 size={14} className="vr-spin-inline" /> Reading…
                </>
              ) : (
                <>
                  Next <ArrowRight size={14} />
                </>
              )}
            </button>
          ) : (
            <button
              className="vr-btn vr-btn-glow"
              onClick={submit}
              // Nothing joins the library half-reviewed. A button that stays grey says so more plainly
              // than a warning after the fact would.
              disabled={outstanding.length > 0 || saving}
              title={
                outstanding.length > 0
                  ? `${outstanding.map((f) => LABELS[f]).join(", ")} still to confirm`
                  : undefined
              }
            >
              {saving ? (
                <>
                  <Loader2 size={14} className="vr-spin-inline" /> Adding…
                </>
              ) : (
                <>
                  <Upload size={14} /> Add to Library
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default UploadStandardModal;
